/**
 * Segnalazioni Feature React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/useAuth';
import * as api from '@/api/queries/segnalazioni';
import type { Segnalazione, SegnalazioneInsert, SegnalazioneUpdate } from './types';

const QUERY_KEY = 'segnalazioni';

// ============================================================
// QUERIES
// ============================================================

export function useSegnalazioni() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, user?.id],
    queryFn: async () => {
      const { data, error } = await api.getSegnalazioni({ userId: user?.id });
      if (error) throw error;
      return (data || []) as Segnalazione[];
    },
    enabled: !!user?.id,
  });
}

export function useSegnalazione(id?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, id, user?.id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api.getSegnalazioneById(id, user?.id);
      if (error) throw error;
      return data as Segnalazione | null;
    },
    enabled: !!user?.id && !!id,
  });
}

// ============================================================
// MUTATIONS
// ============================================================

export function useCreateSegnalazione() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (segnalazione: SegnalazioneInsert): Promise<Segnalazione> => {
      const { data, error } = await api.createSegnalazione(segnalazione);
      if (error) throw error;
      if (!data) throw new Error("Failed to create segnalazione");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateSegnalazione() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SegnalazioneUpdate }): Promise<Segnalazione> => {
      const { data: result, error } = await api.updateSegnalazione(id, data);
      if (error) throw error;
      if (!result) throw new Error("Failed to update segnalazione");
      return result;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useDeleteSegnalazione() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await api.deleteSegnalazione(id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useSegnalazioniStats() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'stats', user?.id],
    queryFn: async () => {
      const { data, error } = await api.getSegnalazioni({ userId: user?.id });
      if (error) throw error;
      const segnalazioni = (data || []) as Segnalazione[];
      
      return {
        totali: segnalazioni.length,
        aperti: segnalazioni.filter(s => s.stato === 'Aperto').length,
        in_lavorazione: segnalazioni.filter(s => s.stato === 'In lavorazione').length,
        risolti: segnalazioni.filter(s => s.stato === 'Risolto').length,
      };
    },
    enabled: !!user?.id,
  });
}
